import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import Button from '@components-clean/core/button';
import { MultiSelectController } from '@components-clean/core/multi-select';
import { MultiSelectEmailController, Person } from '@components-clean/core/multi-select-email/multi-select-email';

interface MultiSelectPageProps {
    isDisabled: boolean;
}

interface MultiSelectOption {
    id: number;
    value: string;
}

const data: MultiSelectOption[] = [
    'Pizza',
    'Burger',
    'Pasta',
    'BurritoBurritoBurritoBurritoBurritoBurritoBurritoBurritoBurritoBurritoBurritoBurritoBurritoBurritoBurritoBurritoBurrito',
    'Cupcake',
    'Cookies',
    'Eggs',
    'Bread',
    'Ham',
    'Dust',
].map((food, index) => ({
    id: index + 1,
    value: food,
}));

type MultiSelectForm = {
    multiSelectSolid: MultiSelectOption[] | null;
    multiSelectSolidRequired: MultiSelectOption[] | null;
    multiSelectSolidFilterable: MultiSelectOption[] | null;
    multiSelectSolidOnlyCustom: MultiSelectOption[] | null;
    multiSelectSolidNoCustom: MultiSelectOption[] | null;
    multiSelectSolidAutoClose: MultiSelectOption[] | null;
    multiSelectSolidHideSelected: MultiSelectOption[] | null;
    multiSelectSolidMaxOneLine: MultiSelectOption[] | null;
    multiSelectEmail: Person[] | null;
};

const MultiSelectPage = ({ isDisabled }: MultiSelectPageProps) => {
    const { control, handleSubmit } = useForm<MultiSelectForm>({
        mode: 'all',
        defaultValues: {
            multiSelectSolid: null,
            multiSelectSolidRequired: null,
            multiSelectSolidFilterable: null,
            multiSelectSolidOnlyCustom: null,
            multiSelectSolidNoCustom: null,
            multiSelectSolidAutoClose: null,
            multiSelectSolidHideSelected: null,
            multiSelectSolidMaxOneLine: null,
            multiSelectEmail: null,
        },
    });

    const onSubmit: SubmitHandler<MultiSelectForm> = (dataHookForm) => {
        // eslint-disable-next-line no-alert
        alert(JSON.stringify(dataHookForm, null, 2));
    };

    return (
        <div className="k-gap-4" style={{ minWidth: 150, maxWidth: 350 }}>
            <form onSubmit={handleSubmit(onSubmit)} className="k-d-flex k-d-flex-col k-gap-4" noValidate>
                <h2 className="k-mb-0 !k-mt-0">Multiselect</h2>

                <MultiSelectController
                    isEditing
                    control={control}
                    name="multiSelectSolid"
                    data={data}
                    data-testid="multiSelectSolid"
                    label="Multiselect Solid - (Optional)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    filterable={false}
                />

                <MultiSelectController
                    isEditing
                    control={control}
                    name="multiSelectSolidRequired"
                    data={data}
                    data-testid="multiSelectSolidRequired"
                    label="Multiselect Solid - (Required)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    required
                />

                <MultiSelectController
                    isEditing
                    control={control}
                    name="multiSelectSolidFilterable"
                    data={data}
                    data-testid="multiSelectSolidFilterable"
                    label="Multiselect Solid - (Filterable)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    filterable
                />

                <MultiSelectController
                    isEditing
                    control={control}
                    name="multiSelectSolidOnlyCustom"
                    data={[]}
                    data-testid="multiSelectSolidOnlyCustom"
                    label="Multiselect Solid - (Only Custom Items)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    filterable={false}
                />

                <MultiSelectController
                    isEditing
                    control={control}
                    name="multiSelectSolidNoCustom"
                    label="Multiselect Solid - (No Custom Items)"
                    data-testid="multiSelectSolidNoCustom"
                    data={data}
                    textField="value"
                    dataItemKey="id"
                    allowCustom={false}
                />

                <MultiSelectController
                    isEditing
                    control={control}
                    name="multiSelectSolidAutoClose"
                    label="Multiselect Solid - (Auto Close)"
                    data-testid="multiSelectSolidAutoClose"
                    data={data}
                    textField="value"
                    dataItemKey="id"
                    allowCustom={false}
                    autoClose
                />

                <MultiSelectController
                    isEditing
                    control={control}
                    name="multiSelectSolidHideSelected"
                    label="Multiselect Solid - (Hide Selected)"
                    data-testid="multiSelectSolidHideSelected"
                    data={data}
                    textField="value"
                    dataItemKey="id"
                />

                <MultiSelectController
                    isEditing
                    control={control}
                    name="multiSelectSolidMaxOneLine"
                    label="Multiselect Solid - (Max One Line)"
                    data-testid="multiSelectSolidMaxOneLine"
                    data={data}
                    textField="value"
                    dataItemKey="id"
                    maxLines={1}
                />

                <MultiSelectEmailController
                    data-testid="multiSelectEmail"
                    label="Email Multiselect"
                    control={control}
                    name="multiSelectEmail"
                    data={[
                        { firstName: 'Stephen', lastName: 'Hewinson', email: 'Stephen.Hewinson@tritrials.com' },
                        { firstName: 'Andy', lastName: 'Rice', email: 'Andy.Rice@tritrials.com' },
                        { firstName: 'Steven', lastName: 'Rajaratnam', email: 'Steven.Rajaratnam@tritrials.com' },
                        { firstName: 'Foo', lastName: 'Bar', email: 'baz@tritrials.com' },
                    ]}
                    allowCustom
                />

                <Button type="submit" data-testid="submit-button" primary disabled={isDisabled}>
                    Submit
                </Button>
            </form>
        </div>
    );
};

export default MultiSelectPage;
